import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Key, 
  Plus, 
  Eye, 
  EyeOff, 
  Copy, 
  CheckCircle, 
  ArrowLeft,
  Shield,
  Code,
  BookOpen,
  AlertTriangle,
  Calendar,
  Activity,
  Check,
  X
} from 'lucide-react';
import { getApiKeys, createApiKey, deactivateApiKey, getCurrentUser } from '../services/api';

const ApiKeys = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);

  useEffect(() => {
    loadUser();
  }, []);

  useEffect(() => {
    if (user?.organization_id) {
      loadApiKeys();
    }
  }, [user]);

  const loadUser = async () => {
    try {
      const userData = await getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Error loading user:', error);
    }
  };

  const loadApiKeys = async () => {
    if (!user?.organization_id) return;

    try {
      setLoading(true);
      const keys = await getApiKeys(user.organization_id);
      setApiKeys(keys);
    } catch (error) {
      console.error('Error loading API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    
    if (!newKeyName.trim()) {
      alert('Please enter a key name');
      return;
    }
    
    if (!user?.organization_id) {
      alert('No organization found. Please refresh the page and try again.');
      return;
    }

    try {
      setCreating(true);
      const newKey = await createApiKey(user.organization_id, newKeyName.trim());
      setNewlyCreatedKey(newKey);
      setNewKeyName('');
      setShowCreateModal(false);
      loadApiKeys(); // Refresh the list
    } catch (error) {
      console.error('Error creating API key:', error);
      alert(`Failed to create API key: ${error.message || 'Unknown error'}`);
    } finally {
      setCreating(false);
    }
  };

  const handleDeactivateKey = async (keyId, keyName) => {
    if (!window.confirm(`Are you sure you want to deactivate the API key "${keyName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deactivateApiKey(keyId);
      loadApiKeys(); // Refresh the list
    } catch (error) {
      console.error('Error deactivating API key:', error);
      alert('Failed to deactivate API key. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('API key copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy to clipboard. Please copy manually.');
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100/50 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-900/80 shadow rounded-2xl backdrop-blur-sm border-b border-gray-200/50 dark:border-gray-700/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors"
                >
                  <ArrowLeft className="h-5 w-5" />
                  <span className="text-sm font-medium">Back to Dashboard</span>
                </button>
                <div className="h-6 w-px bg-gray-300 dark:bg-gray-600" />
                <div>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-celadon-500 to-celadon-600 rounded-xl flex items-center justify-center shadow-lg">
                      <Key className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">API Keys</h1>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Manage organization-level API keys for the Banyan SDK
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center space-x-2 bg-gradient-to-r from-celadon-500 to-celadon-600 text-white px-4 py-2.5 rounded-xl font-medium shadow-lg hover:shadow-xl hover:from-celadon-600 hover:to-celadon-700 transform hover:scale-105 transition-all duration-200"
              >
                <Plus className="h-4 w-4" />
                <span>Create API Key</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* SDK Setup Instructions */}
        <div className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center space-x-4 mb-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Getting Started with the Python SDK</h3>
              <p className="text-gray-600 dark:text-gray-400">Connect your applications with Banyan</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-1 gap-8">
            <div className="space-y-6">
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-6 h-6 bg-celadon-100 dark:bg-celadon-900/30 text-celadon-700 dark:text-celadon-300 rounded-full flex items-center justify-center text-sm font-semibold">1</div>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">Install the SDK</p>
                </div>
                <div className="bg-gray-900 dark:bg-gray-800 border border-gray-700 rounded-xl p-4">
                  <code className="text-green-400 font-mono text-sm">pip install banyan-sdk</code>
                </div>
              </div>

              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-6 h-6 bg-celadon-100 dark:bg-celadon-900/30 text-celadon-700 dark:text-celadon-300 rounded-full flex items-center justify-center text-sm font-semibold">2</div>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">Configure with your API key</p>
                </div>
                <div className="bg-gray-900 dark:bg-gray-800 border border-gray-700 rounded-xl p-4 overflow-x-auto">
                  <pre className="text-gray-300 font-mono text-xs leading-relaxed">
{`import banyan
import os

# Organization-level configuration
banyan.configure(
    api_key=os.getenv('BANYAN_API_KEY'),
)

# Get a prompt
prompt_data = banyan.get_prompt(
    name="welcome-message"
)`}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* API Keys List */}
        <div className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-2xl shadow-xl overflow-hidden">
          <div className="px-8 py-6 border-b border-gray-200/50 dark:border-gray-700/50">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-celadon-500 to-celadon-600 rounded-lg flex items-center justify-center">
                <Key className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Your API Keys</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {apiKeys.length} key{apiKeys.length !== 1 ? 's' : ''} configured
                </p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-2 border-celadon-500 border-t-transparent"></div>
              <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">Loading API keys...</p>
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Key className="h-8 w-8 text-gray-400" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No API keys found</h4>
              <p className="text-gray-600 dark:text-gray-400 mb-6">Create your first API key to get started with the SDK</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-celadon-500 to-celadon-600 text-white px-4 py-2 rounded-lg font-medium hover:from-celadon-600 hover:to-celadon-700 transition-all"
              >
                <Plus className="h-4 w-4" />
                <span>Create Your First Key</span>
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200/50 dark:divide-gray-700/50">
              {apiKeys.map((key) => (
                <div 
                  key={key.id} 
                  className={`p-6 hover:bg-gray-50/50 dark:hover:bg-gray-700/20 transition-colors ${
                    !key.is_active ? 'opacity-60' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-3">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {key.key_name}
                        </h4>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          key.is_active
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                        }`}>
                          <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                            key.is_active ? 'bg-green-400' : 'bg-red-400'
                          }`} />
                          {key.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="flex items-center space-x-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                          <Copy className="h-4 w-4 text-gray-400" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Key Preview</p>
                            <code className="text-sm font-mono text-gray-900 dark:text-gray-100 block truncate">
                              {key.masked_key}
                            </code>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                          <Activity className="h-4 w-4 text-gray-400" />
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Usage Count</p>
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {key.usage_count || 0}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Created</p>
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {new Date(key.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Last used: {key.last_used_at ? formatDate(key.last_used_at) : 'Never'}
                        </div>
                        {key.is_active && (
                          <button
                            onClick={() => handleDeactivateKey(key.id, key.key_name)}
                            className="inline-flex items-center space-x-1 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium transition-colors"
                          >
                            <X className="h-3 w-3" />
                            <span>Deactivate</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md border border-gray-200/50 dark:border-gray-700/50">
            <div className="p-6 border-b border-gray-200/50 dark:border-gray-700/50">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-celadon-500 to-celadon-600 rounded-xl flex items-center justify-center">
                  <Plus className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Create New API Key</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Generate a new key for SDK access</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleCreateKey} className="p-6">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Key Name
                </label>
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., Production App, Development, etc."
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-celadon-500 focus:border-celadon-500"
                  required
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Choose a descriptive name to identify this key
                </p>
              </div>

              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-amber-900 dark:text-amber-100">Important</h4>
                    <p className="text-xs text-amber-800 dark:text-amber-200 mt-1">
                      The API key will only be shown once. Make sure to copy and store it securely.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewKeyName('');
                  }}
                  className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !newKeyName.trim()}
                  className="px-4 py-2.5 bg-gradient-to-r from-celadon-500 to-celadon-600 text-white rounded-xl text-sm font-medium hover:from-celadon-600 hover:to-celadon-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {creating ? 'Creating...' : 'Create Key'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Key Created Modal */}
      {newlyCreatedKey && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg border border-gray-200/50 dark:border-gray-700/50">
            <div className="p-6 border-b border-gray-200/50 dark:border-gray-700/50">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">API Key Created Successfully!</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Your new API key is ready to use</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Your API Key (copy this now - it won't be shown again):
                </label>
                <div className="bg-gray-900 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-4 mb-3">
                  <code className="text-sm font-mono text-green-400 break-all block">
                    {newlyCreatedKey.key_value}
                  </code>
                </div>
                <button
                  onClick={() => copyToClipboard(newlyCreatedKey.key_value)}
                  className="inline-flex items-center space-x-2 text-sm text-celadon-600 dark:text-celadon-400 hover:text-celadon-700 dark:hover:text-celadon-300 font-medium transition-colors"
                >
                  <Copy className="h-4 w-4" />
                  <span>Copy to clipboard</span>
                </button>
              </div>

              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-amber-900 dark:text-amber-100">Security Note</h4>
                    <p className="text-xs text-amber-800 dark:text-amber-200 mt-1">
                      Store this key securely. It won't be displayed again for security reasons. 
                      Use environment variables in production.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setNewlyCreatedKey(null)}
                  className="px-6 py-2.5 bg-gradient-to-r from-celadon-500 to-celadon-600 text-white rounded-xl text-sm font-medium hover:from-celadon-600 hover:to-celadon-700 transition-all"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeys; 